import { MEDIA_LIMITS } from "./contract.ts";
import type { ClipboardSet, Direction, FileChunk, FileOffer } from "./types.ts";

export type ReviewFailure = "disabled" | "limit" | "size-mismatch" | "bad-payload";

export type Review = { ok: true } | { ok: false; reason: ReviewFailure };

export function base64ByteLength(payload: string): number | undefined {
  if (payload.length % 4 !== 0) return undefined;
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(payload)) return undefined;
  try {
    return atob(payload).length;
  } catch {
    return undefined;
  }
}

export function reviewClipboardSet(input: { message: ClipboardSet; clipboardEnabled: boolean }): Review {
  if (!input.clipboardEnabled) return { ok: false, reason: "disabled" };
  const decoded = base64ByteLength(input.message.payloadBase64);
  if (decoded === undefined) return { ok: false, reason: "bad-payload" };
  if (input.message.byteLength > MEDIA_LIMITS.maxClipboardBytes || decoded > MEDIA_LIMITS.maxClipboardBytes) {
    return { ok: false, reason: "limit" };
  }
  if (decoded !== input.message.byteLength) return { ok: false, reason: "size-mismatch" };
  return { ok: true };
}

export function reviewFileOffer(input: { offer: FileOffer; fileTransferEnabled: boolean; activeTransfers: number }): Review {
  if (!input.fileTransferEnabled) return { ok: false, reason: "disabled" };
  if (input.activeTransfers >= MEDIA_LIMITS.maxConcurrentFileTransfersPerSession) {
    return { ok: false, reason: "limit" };
  }
  if (input.offer.bytes > MEDIA_LIMITS.maxFileBytes || input.offer.chunkBytes !== MEDIA_LIMITS.fileChunkBytes) {
    return { ok: false, reason: "limit" };
  }
  return { ok: true };
}

export function reviewFileChunk(input: { chunk: FileChunk }): Review {
  const decoded = base64ByteLength(input.chunk.payloadBase64);
  if (decoded === undefined || decoded === 0) return { ok: false, reason: "bad-payload" };
  if (decoded > MEDIA_LIMITS.fileChunkBytes) return { ok: false, reason: "limit" };
  return { ok: true };
}

export function fileHashDecision(expectedSha256: string, actualSha256: string): "ack" | "hash-mismatch" {
  return expectedSha256 === actualSha256 ? "ack" : "hash-mismatch";
}

export function clipboardAudit(features: { clipboard: boolean }): { clipboardEnabled: boolean } {
  return { clipboardEnabled: features.clipboard };
}

export function fileTransferAudit(offer: {
  direction: Direction;
  name: string;
  sha256: string;
  bytes: number;
}): { direction: Direction; name: string; sha256: string; bytes: number } {
  return {
    direction: offer.direction,
    name: offer.name,
    sha256: offer.sha256,
    bytes: offer.bytes,
  };
}
